import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";

import { useARC } from "providers/ARC/ARCProvider";
import { ConnectionState } from "lib/consts/ARC/connection";
import {
    attachRosTopicWithRetry,
    LATE_ROS_TOPIC_OPTIONS,
} from "lib/ros/rosTopicSubscribeWithRetry";
import { getActionCallerBySocket } from "lib/utils/connection";
import { ActionHandle } from "arcjs/action/base";

const DOCKING_ACTION_NAME = "/docking";
const DOCKING_STATE_TOPIC = "/docking/state";
const DOCKING_CHARGE_STATUS_TOPIC = "/docking/charge_status";

interface ActionResult {
    success?: boolean;
    message?: string;
    error?: string;
    [key: string]: unknown;
}

interface ActionFeedback {
    stage?: string;
    range_m?: number;
    active_tag_id?: number;
    operation?: string;
    [key: string]: unknown;
}

interface DockingContextType {
    /** Live docked from /docking/state (charge pile pins/contacts; not SIT/WALK). */
    isDocked: boolean;
    /** Latest charge pile status line from /docking/charge_status. */
    chargeStatus: string | null;
    actionStatus: string | null;
    actionFeedback: ActionFeedback | null;
    actionResult: ActionResult | null;
    showActionFeedback: boolean;
    isActionValid: boolean;
    activeActionLabel: string | null;
    triggerDocking: () => void;
    handleActionCancel: () => void;
    handleForceClose: () => void;
    setShowActionFeedback: (show: boolean) => void;
}

const DockingContext = createContext<DockingContextType | undefined>(undefined);
DockingContext.displayName = "Docking";

export const useDocking = (): DockingContextType => {
    const context = useContext(DockingContext);
    if (!context) {
        throw new Error("useDocking must be used within DockingProvider");
    }
    return context;
};

interface DockingProviderProps {
    children: ReactNode;
}

const DockingProvider = ({ children }: DockingProviderProps) => {
    const { controlSocket, connectionState } = useARC();
    const actionCaller = useMemo(
        () => getActionCallerBySocket(controlSocket),
        [controlSocket],
    );

    const [dockingStateFromTopic, setDockingStateFromTopic] = useState<boolean | null>(null);
    const [chargeStatus, setChargeStatus] = useState<string | null>(null);
    const [actionStatus, setActionStatus] = useState<string | null>(null);
    const [actionResult, setActionResult] = useState<ActionResult | null>(null);
    const [actionFeedback, setActionFeedback] = useState<ActionFeedback | null>(null);
    const [goalId, setGoalId] = useState<string | null>(null);
    const handleRef = useRef<ActionHandle | null>(null);
    const [activeActionLabel, setActiveActionLabel] = useState<string | null>(null);
    const [showActionFeedback, setShowActionFeedback] = useState(false);
    const [isActionValid, setIsActionValid] = useState(true);

    const isDocked = dockingStateFromTopic === true;

    const resetActionState = useCallback(() => {
        setActionStatus(null);
        setActionResult(null);
        setActionFeedback(null);
        setGoalId(null);
        handleRef.current = null;
        setActiveActionLabel(null);
        setIsActionValid(false);
        setShowActionFeedback(false);
    }, []);

    useEffect(() => {
        if (!controlSocket || connectionState !== ConnectionState.Connected) {
            setDockingStateFromTopic(null);
            setChargeStatus(null);
            return;
        }

        const cleanupState = attachRosTopicWithRetry(
            controlSocket,
            DOCKING_STATE_TOPIC,
            (data) => {
                if (typeof data === "boolean") {
                    setDockingStateFromTopic(data);
                    return;
                }
                if (data && typeof data === "object" && "data" in data) {
                    setDockingStateFromTopic(Boolean((data as { data: boolean }).data));
                }
            },
            {
                ...LATE_ROS_TOPIC_OPTIONS,
                messageTypeFallback: "std_msgs/Bool",
            },
        );

        const cleanupCharge = attachRosTopicWithRetry(
            controlSocket,
            DOCKING_CHARGE_STATUS_TOPIC,
            (data) => {
                if (typeof data === "string") {
                    setChargeStatus(data.trim() || null);
                    return;
                }
                if (data && typeof data === "object" && "data" in data) {
                    const raw = String((data as { data: unknown }).data ?? "").trim();
                    setChargeStatus(raw || null);
                }
            },
            {
                ...LATE_ROS_TOPIC_OPTIONS,
                messageTypeFallback: "std_msgs/String",
            },
        );

        return () => {
            cleanupState();
            cleanupCharge();
        };
    }, [controlSocket, connectionState]);

    const handleActionCancel = useCallback(() => {
        if (connectionState !== ConnectionState.Connected || !isActionValid) {
            resetActionState();
            return;
        }

        try {
            if (!handleRef.current) {
                resetActionState();
                return;
            }
            void handleRef.current.cancel();
        } catch (error) {
            console.error("[DockingProvider] Cancel failed:", error);
            resetActionState();
        }
    }, [connectionState, isActionValid, resetActionState]);

    const handleForceClose = useCallback(() => {
        resetActionState();
    }, [resetActionState]);

    const triggerDocking = useCallback(() => {
        if (
            !actionCaller ||
            connectionState !== ConnectionState.Connected ||
            actionStatus === "active"
        ) {
            return;
        }

        const goal = {
            standoff_distance: 0,
            target_range_m: 0,
            show_viewer: false,
        };

        console.log("[DockingProvider] Sending docking goal:", goal, {
            isDocked,
            dockingStateFromTopic,
        });

        actionCaller
            .sendGoal<typeof goal, ActionFeedback, ActionResult>(DOCKING_ACTION_NAME, goal, {
                onResult: (result) => {
                    setActionStatus("succeeded");
                    setActionResult(result as ActionResult);
                },
                onFeedback: (feedback) => {
                    const parsed = feedback as ActionFeedback;
                    setActionFeedback(parsed);
                    if (parsed.operation === "dock" || parsed.operation === "undock") {
                        setActiveActionLabel(
                            parsed.operation === "undock" ? "Undock" : "Dock",
                        );
                    }
                    setIsActionValid(true);
                },
                onFailed: (error) => {
                    const errorStr = String(error);
                    const lowerError = errorStr.toLowerCase();
                    let parsedResult: ActionResult = { error: errorStr };
                    const jsonMatch = errorStr.match(/\{[\s\S]*\}$/);
                    if (jsonMatch) {
                        try {
                            parsedResult = JSON.parse(jsonMatch[0]) as ActionResult;
                        } catch {
                            parsedResult = { error: errorStr };
                        }
                    }
                    if (
                        lowerError.includes("canceled") ||
                        lowerError.includes("status_canceled") ||
                        lowerError.includes("was canceled")
                    ) {
                        setActionStatus("canceled");
                    } else {
                        setActionStatus("failed");
                    }
                    setActionResult(parsedResult);
                },
            })
            .then((handle) => {
                handleRef.current = handle;
                setGoalId(handle.goalId);
            })
            .catch((error) => {
                setActionStatus("failed");
                setActionResult({ error: String(error) });
                setIsActionValid(false);
            });

        setActiveActionLabel(isDocked ? "Undock" : "Dock");
        setActionStatus("active");
        setActionFeedback(null);
        setActionResult(null);
        setShowActionFeedback(true);
        setIsActionValid(true);
    }, [
        actionCaller,
        connectionState,
        actionStatus,
        isDocked,
        dockingStateFromTopic,
    ]);

    useEffect(() => {
        if (connectionState === ConnectionState.Disconnected && goalId) {
            setIsActionValid(false);
            setActionStatus("failed");
            setActionResult({
                error: "Connection lost. Action may be invalid. Please retry after reconnecting.",
            });
        }
    }, [connectionState, goalId]);

    const value: DockingContextType = {
        isDocked,
        chargeStatus,
        actionStatus,
        actionFeedback,
        actionResult,
        showActionFeedback,
        isActionValid,
        activeActionLabel,
        triggerDocking,
        handleActionCancel,
        handleForceClose,
        setShowActionFeedback,
    };

    return <DockingContext.Provider value={value}>{children}</DockingContext.Provider>;
};

export default DockingProvider;
